import { Menu, Table } from '@mantine/core';
import { IconInfoCircle, IconPassword, IconTrash } from '@tabler/icons-react';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { ADMIN, BRISTEC_ADMIN, MODERATOR } from '../../../const/constants';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { formatToDateString } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { AccountDto, UpsertAccountDto } from '../types/AccountDto';
import { EditAccountModal } from './EditAccountModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface AccountRowProps {
	initialAccount: AccountDto;
	triggerListUpdate: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	userRole: string;
}

export function AccountRow({
	initialAccount,
	triggerListUpdate,
	userRole,
}: AccountRowProps) {
	const { organisation } = useParams();

	const [account, setAccount] = useState(initialAccount);

	const passwordModal = useModal();
	const deleteModal = useModal();
	const editModal = useModal();

	const deleteAccountMutation = mutate<boolean>(
		`${organisation}/user/delete/${account.id.toString()}`,
	);

	return (
		<>
			<ResetPasswordModal
				close={passwordModal.close}
				opened={passwordModal.isOpen}
				accountId={account.id.toString()}
				organisation={organisation}
			/>
			<EditAccountModal
				close={editModal.close}
				opened={editModal.isOpen}
				defaultAccount={mapAccountDtoToUpsertDto(account)}
				updateTableValue={setAccount}
			/>
			<DeleteModal
				close={deleteModal.close}
				isOpened={deleteModal.isOpen}
				titleType={'Account'}
				itemDescription={account.username}
				mutation={deleteAccountMutation}
				onComplete={(): void => {
					triggerListUpdate();
				}}
			/>
			<KeyedRow>
				<Table.Td>{undefinedCheck(account.firstName)}</Table.Td>
				<Table.Td>{undefinedCheck(account.lastName)}</Table.Td>
				<Table.Td>{undefinedCheck(account.username)}</Table.Td>
				<Table.Td>{undefinedCheck(account.email)}</Table.Td>
				<Table.Td>{undefinedCheck(account.roleName)}</Table.Td>
				<Table.Td>{formatToDateString(account.dateCreated)}</Table.Td>
				<Table.Td>{formatToDateString(account.lastSignIn)}</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconInfoCircle {...defaultIconStyle} />}
							onClick={() => editModal.open()}
							data-cy={'edit-button'}
							disabled={
								![ADMIN, BRISTEC_ADMIN].includes(userRole.toLowerCase())
							}
						>
							Edit Account
						</Menu.Item>
						<Menu.Item
							leftSection={<IconPassword {...defaultIconStyle} />}
							onClick={() => passwordModal.open()}
							data-cy={'resetPassword-button'}
							disabled={
								![MODERATOR, ADMIN, BRISTEC_ADMIN].includes(
									userRole.toLowerCase(),
								)
							}
						>
							Reset Password
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
							data-cy={'delete-button'}
							disabled={
								![ADMIN, BRISTEC_ADMIN].includes(userRole.toLowerCase())
							}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
		</>
	);
}

function mapAccountDtoToUpsertDto(account: AccountDto): UpsertAccountDto {
	return {
		id: account.id.toString(), // Convert number to string
		username: account.username,
		email: account.email,
		firstName: account.firstName,
		lastName: account.lastName,
		password: '', // Set a default or leave it empty
		checkPassword: '', // Same as password
		roleId: account.roleId,
		branchIds: account.branchIds || [], // Ensure it remains an array
		isEdit: true,
	};
}
